import { useCallback, useMemo } from 'react';
import { ListRenderItemInfo } from 'react-native';

import { Carousel } from '@fhs/ui';
import { ClickEventComponentNames, EventTypes } from '@fhs-legacy/frontend/src/state/crm-events';
import { CustomEventNames } from '@fhs-legacy/frontend/src/state/crm-events';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';

import { ChallengeClickEventLabels } from '../../analytics';
import {
  ChallengeCard,
  ChallengeCardProps,
  ChallengeCardSkeleton,
  useChallengeCardWidth,
} from '../../components';
import { useOnPressChallenge } from '../../hooks/use-on-press-challenge';
import { useChallengeDetailsModalStore } from '../../modals';
import { useChallengesWithProgressUi } from '../../state/challenges-ui-helper-state';
import { getChallengeBadge } from '../../utils';
import { getChallengeButtonText } from '../../utils';
import { getChallengeDescription } from '../../utils';

const renderChallengeCard = ({ item }: ListRenderItemInfo<ChallengeCardProps>) => (
  <ChallengeCard {...item} />
);

export function Challenges({ isLoading }: { isLoading: boolean }) {
  const { data } = useChallengesWithProgressUi();
  const { show } = useChallengeDetailsModalStore();
  const { logRBIEvent } = useCRMEventsContext();
  const onPressChallenge = useOnPressChallenge();
  const itemWidth = useChallengeCardWidth();

  const challengesCardProps = useMemo(
    () =>
      data?.map<ChallengeCardProps>(challenge => ({
        image: challenge.image,
        title: challenge.name ?? '',
        description: getChallengeDescription(challenge),
        onPress: async () => await onPressChallenge(challenge),
        buttonText: getChallengeButtonText(challenge.status),
        progress: challenge.progress,
        badge: getChallengeBadge(challenge.status, challenge.progress),
        onPressInfo: () => {
          show(challenge.id);
          logRBIEvent({
            name: CustomEventNames.CLICK_EVENT,
            type: EventTypes.Other,
            attributes: {
              component: ClickEventComponentNames.BUTTON,
              text: ChallengeClickEventLabels.OPEN_INFO,
              headerText: challenge.name ?? undefined,
            },
          });
        },
      })) ?? [],
    [data, onPressChallenge, show, logRBIEvent]
  );

  const renderCarouselLoading = useCallback(() => {
    return <ChallengeCardSkeleton style={{ alignSelf: 'center', marginTop: 16 }} />;
  }, []);

  if (isLoading) {
    return renderCarouselLoading();
  }

  return (
    <Carousel
      renderCarouselLoading={renderCarouselLoading}
      style={{ marginTop: 16 }}
      enableNativeSnap
      data={challengesCardProps}
      itemWidth={itemWidth}
      renderItem={renderChallengeCard}
      scrollEnabled={challengesCardProps.length > 1}
    />
  );
}
